import axiosInstance from '@/setup/axios/axiosInstance';

export type StatusMeta = {
  name: string;
  color?: string;
};

export type StatusOption = StatusMeta & {
  id: string;
  is_default?: boolean;
  sort_order?: number;
  group?: {
    id?: string;
    name?: string;
    sort_order?: number;
  };
};

/** @deprecated Use StatusOption */
export type TransactionStatusOption = StatusOption;

const FINANCE_DEFAULT_MODULES = ['finance', 'transaction'] as const;
const EMPLOYEE_DEFAULT_MODULES = ['employee', 'employees'] as const;

const modulePayloadCache = new Map<string, Promise<unknown>>();

const resolveModulePayload = (data: Record<string, unknown>, moduleName: string): unknown => {
  return (
    data?.[moduleName] ||
    data?.finance ||
    data?.transactions ||
    data?.transaction ||
    data?.employee ||
    data?.employees ||
    data
  );
};

async function fetchModulePayload(moduleName: string): Promise<unknown> {
  const response = await axiosInstance.get(`/settings/statuses/by-module/?module=${moduleName}`);
  const data = response.data as Record<string, unknown>;
  return resolveModulePayload(data, moduleName);
}

const parseStatusItemsToMetaMap = (modulePayload: unknown): Record<string, StatusMeta> => {
  const map: Record<string, StatusMeta> = {};

  const pushItem = (item: any) => {
    const id = item?.id != null ? String(item.id) : '';
    const name = typeof item?.name === 'string' ? item.name.trim() : '';
    const color = typeof item?.color === 'string' ? item.color.trim() : '';
    if (id && name) {
      map[id] = { name, color: color || undefined };
    }
  };

  const payload = modulePayload as any;
  if (Array.isArray(payload?.groups)) {
    payload.groups.forEach((group: any) => {
      const items = Array.isArray(group?.items) ? group.items : [];
      items.forEach(pushItem);
    });
  } else if (Array.isArray(payload?.items)) {
    payload.items.forEach(pushItem);
  } else if (Array.isArray(payload)) {
    payload.forEach(pushItem);
  }

  return map;
};

const parseStatusItemsToOptions = (modulePayload: unknown): StatusOption[] => {
  const payload = modulePayload as any;

  if (Array.isArray(payload?.groups)) {
    return payload.groups.flatMap((group: any) => {
      const groupStatus = group?.group_status || null;
      const items = Array.isArray(group?.items) ? group.items : [];
      return items.map((item: any) => ({
        id: String(item.id),
        name: item.name || '',
        color: item.color || undefined,
        is_default: !!item.is_default,
        sort_order: item.sort_order ?? 0,
        group: groupStatus
          ? {
              id: groupStatus.id,
              name: groupStatus.name,
              sort_order: groupStatus.sort_order ?? 999,
            }
          : undefined,
      }));
    });
  }

  if (Array.isArray(payload?.items)) {
    return payload.items.map((item: any) => ({
      id: String(item.id),
      name: item.name || '',
      color: item.color || undefined,
      is_default: !!item.is_default,
      sort_order: item.sort_order ?? 0,
      group: item?.group_status
        ? {
            id: item.group_status.id,
            name: item.group_status.name,
            sort_order: item.group_status.sort_order ?? 999,
          }
        : undefined,
    }));
  }

  if (Array.isArray(payload)) {
    return payload.map((item: any) => ({
      id: String(item.id),
      name: item.name || '',
      color: item.color || undefined,
      is_default: !!item.is_default,
      sort_order: item.sort_order ?? 0,
    }));
  }

  return [];
};

async function getModulePayload(moduleCandidates: string[]): Promise<unknown> {
  const key = moduleCandidates.join('|');
  const cached = modulePayloadCache.get(key);
  if (cached) {
    return cached;
  }

  const promise = (async () => {
    for (const moduleName of moduleCandidates) {
      try {
        const modulePayload = await fetchModulePayload(moduleName);
        const map = parseStatusItemsToMetaMap(modulePayload);
        const options = parseStatusItemsToOptions(modulePayload);
        if (Object.keys(map).length > 0 || options.length > 0) {
          return modulePayload;
        }
      } catch {
        // try next module
      }
    }
    return null;
  })();

  modulePayloadCache.set(key, promise);
  return promise;
}

export async function getStatusMetaByModule(
  moduleCandidates: string[] = [...FINANCE_DEFAULT_MODULES]
): Promise<Record<string, StatusMeta>> {
  const modulePayload = await getModulePayload(moduleCandidates);
  return parseStatusItemsToMetaMap(modulePayload);
}

export async function getStatusOptionsByModule(
  moduleCandidates: string[] = [...FINANCE_DEFAULT_MODULES]
): Promise<StatusOption[]> {
  const modulePayload = await getModulePayload(moduleCandidates);
  return parseStatusItemsToOptions(modulePayload);
}

export const EMPLOYEE_STATUS_MODULES = [...EMPLOYEE_DEFAULT_MODULES];
export const FINANCE_STATUS_MODULES = [...FINANCE_DEFAULT_MODULES];

export function clearStatusByModuleCache(): void {
  modulePayloadCache.clear();
}
